// ═══════════════════════════════════════════════════════
// Premium Game SFX — Web Audio API Synthesizer
// Rich, layered sounds with reverb, harmonics & ADSR
// Zero external files — instant load, any device
// ═══════════════════════════════════════════════════════

let ctx: AudioContext | null = null;
let sfxMuted = false;
let sfxVolume = 0.35;
let reverbNode: ConvolverNode | null = null;
let audioUnlocked = false;

// ── Audio Engine Core ──────────────────────────────────

function getCtx(): AudioContext {
  if (!ctx) ctx = new AudioContext();
  if (ctx.state === 'suspended') ctx.resume();
  return ctx;
}

/**
 * Unlock AudioContext on first user interaction.
 * Must be called from a click/touchstart handler.
 * This ensures SFX work on Android WebView.
 */
export function unlockAudio() {
  if (audioUnlocked) return;
  try {
    const c = getCtx();
    // Play a silent buffer to unlock
    const buffer = c.createBuffer(1, 1, c.sampleRate);
    const source = c.createBufferSource();
    source.buffer = buffer;
    source.connect(c.destination);
    source.start(0);
    source.stop(0.001);
    audioUnlocked = true;
    if (import.meta.env.DEV) console.log('[SFX] AudioContext unlocked');
  } catch { /* silent */ }
}

// Auto-unlock on first touch/click (covers Android WebView)
if (typeof document !== 'undefined') {
  const unlock = () => {
    unlockAudio();
    document.removeEventListener('touchstart', unlock, true);
    document.removeEventListener('click', unlock, true);
  };
  document.addEventListener('touchstart', unlock, true);
  document.addEventListener('click', unlock, true);
}

/** Generate a synthetic impulse response for reverb */
function createReverb(c: AudioContext, decay = 1.5, wet = 0.25): ConvolverNode {
  const rate = c.sampleRate;
  const length = rate * decay;
  const impulse = c.createBuffer(2, length, rate);
  for (let ch = 0; ch < 2; ch++) {
    const data = impulse.getChannelData(ch);
    for (let i = 0; i < length; i++) {
      // Exponential decay with random noise = natural room reverb
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, 2.5) * wet;
    }
  }
  const convolver = c.createConvolver();
  convolver.buffer = impulse;
  return convolver;
}

function getReverb(): ConvolverNode {
  const c = getCtx();
  if (!reverbNode) {
    reverbNode = createReverb(c, 1.2, 0.15); // Reduced reverb wet for mobile
    reverbNode.connect(c.destination);
  }
  return reverbNode;
}

/** Create a gain node with master volume applied */
function masterGain(c: AudioContext, vol: number = 1): GainNode {
  const g = c.createGain();
  g.gain.value = sfxVolume * vol;
  return g;
}

/** Detect if running in a mobile WebView (Capacitor/Android) */
function isMobileWebView(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes('wv') || ua.includes('capacitor') || /android.*mobile/i.test(ua);
}

/** Play a tone with ADSR envelope, optional reverb send */
function playTone(opts: {
  freq: number;
  type: OscillatorType;
  startTime: number;
  attack?: number;
  sustain?: number;
  release?: number;
  volume?: number;
  detune?: number;
  pan?: number;
  reverb?: boolean;
  filter?: { type: BiquadFilterType; freq: number; Q?: number };
}) {
  const c = getCtx();
  if (c.state === 'suspended') return; // Don't queue sounds if context is locked

  const {
    freq, type, startTime,
    attack = 0.01, sustain = 0.1, release = 0.15,
    volume = 1, detune = 0, pan = 0, reverb = true, filter
  } = opts;

  // On mobile WebView, skip reverb send to prevent crackling
  const useReverb = reverb && !isMobileWebView();
  // Slightly lower volume on mobile to prevent clipping
  const mobileAttenuation = isMobileWebView() ? 0.7 : 1.0;

  const osc = c.createOscillator();
  osc.type = type;
  osc.frequency.value = freq;
  if (detune) osc.detune.value = detune;

  const env = c.createGain();
  const peakVol = sfxVolume * volume * mobileAttenuation;
  const peak = Math.max(peakVol, 0.002);
  const releaseEnd = startTime + attack + sustain + release;
  // Linear from true zero: a 3-6ms straight attack has no onset step.
  env.gain.setValueAtTime(0, startTime);
  env.gain.linearRampToValueAtTime(peak, startTime + attack);
  env.gain.setValueAtTime(peak, startTime + attack + sustain);
  // Exponential decay is the natural shape, but it can never reach zero.
  // Stopping the oscillator at 0.001 cut the wave mid-cycle, and on desktop
  // the reverb convolver turned that step into a metallic tick at the very
  // end of every note. Finish with a short linear ramp to a true zero, and
  // only stop the oscillator after that.
  env.gain.exponentialRampToValueAtTime(0.001, releaseEnd);
  env.gain.linearRampToValueAtTime(0, releaseEnd + 0.02);

  let chain: AudioNode = osc;

  // Optional filter
  if (filter) {
    const bq = c.createBiquadFilter();
    bq.type = filter.type;
    bq.frequency.value = filter.freq;
    if (filter.Q) bq.Q.value = filter.Q;
    chain.connect(bq);
    chain = bq;
  }

  chain.connect(env);

  // Stereo panning
  if (pan !== 0 && c.createStereoPanner) {
    const panner = c.createStereoPanner();
    panner.pan.value = pan;
    env.connect(panner);
    panner.connect(c.destination);
    if (useReverb) env.connect(getReverb());
  } else {
    env.connect(c.destination);
    if (useReverb) env.connect(getReverb());
  }

  osc.start(startTime);
  osc.stop(releaseEnd + 0.04);
}

/** White noise burst (used for texture/impact) */
function playNoise(opts: {
  startTime: number;
  duration: number;
  volume?: number;
  filterFreq?: number;
  filterType?: BiquadFilterType;
}) {
  const c = getCtx();
  if (c.state === 'suspended') return;

  const { startTime, duration, volume = 0.1, filterFreq = 4000, filterType = 'bandpass' } = opts;

  // On mobile, reduce noise volume further to avoid harshness
  const mobileAttenuation = isMobileWebView() ? 0.5 : 1.0;

  const bufferSize = Math.max(1, Math.floor(c.sampleRate * duration));
  const buffer = c.createBuffer(1, bufferSize, c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = Math.random() * 2 - 1;
  }

  const source = c.createBufferSource();
  source.buffer = buffer;

  const filter = c.createBiquadFilter();
  filter.type = filterType;
  filter.frequency.value = filterFreq;
  filter.Q.value = 1;

  const env = c.createGain();
  const peak = sfxVolume * volume * mobileAttenuation;
  env.gain.setValueAtTime(Math.max(peak, 0.001), startTime);
  env.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

  source.connect(filter);
  filter.connect(env);
  env.connect(c.destination);

  source.start(startTime);
  source.stop(startTime + duration + 0.01);
}

// ── Public API ─────────────────────────────────────────

export function setSfxMuted(m: boolean) { sfxMuted = m; }
export function setSfxVolume(v: number) { sfxVolume = v; }

// ── CORRECT ANSWER ─────────────────────────────────────
// A warm two-note kalimba "ding", built for a PHONE SPEAKER.
//
// The old version read as metallic on real devices for three reasons:
// detuned high partials beat against the fundamentals' harmonics (beating
// IS the metallic sound), a parallel fifth rang up top, and an 8kHz noise
// burst added hiss. All partials here are EXACT harmonics (x1, x2, x4),
// nothing is detuned, there is no noise, and every note is a plucked
// attack with a pure exponential decay (sustain: 0), never an organ-like
// plateau. The x4 partial is lowpassed so it pings without glass.
export function sfxCorrect() {
  if (sfxMuted) return;
  const c = getCtx();
  const t = c.currentTime;

  const note = (freq: number, at: number, vol: number, tail: number) => {
    // fundamental: the body of the note
    playTone({ freq, type: 'sine', startTime: at, attack: 0.004, sustain: 0, release: tail, volume: vol });
    // 2nd harmonic: presence on a small speaker
    playTone({ freq: freq * 2, type: 'sine', startTime: at, attack: 0.004, sustain: 0, release: tail * 0.45, volume: vol * 0.32 });
    // 4th harmonic: the soft "tine" ping, rounded off above 5kHz
    playTone({
      freq: freq * 4, type: 'sine', startTime: at, attack: 0.003, sustain: 0, release: tail * 0.2,
      volume: vol * 0.10, filter: { type: 'lowpass', freq: 5000 },
    });
    // low warmth an octave down, felt more than heard
    playTone({
      freq: freq / 2, type: 'triangle', startTime: at, attack: 0.006, sustain: 0, release: tail * 0.5,
      volume: vol * 0.20, filter: { type: 'lowpass', freq: 900 },
    });
  };

  // C5 then G5: a rising fifth, the oldest "you got it" motif there is.
  note(523.25, t, 0.5, 0.45);
  note(783.99, t + 0.1, 0.42, 0.6);
}

// ── WRONG ANSWER ───────────────────────────────────────
// Soft "bwomp" — noticeable but not harsh or punishing
// Two descending tones with warm filter
export function sfxWrong() {
  if (sfxMuted) return;
  const c = getCtx();
  const t = c.currentTime;

  // Layer 1: Descending "bwomp" (Eb4 → Bb3)
  const osc1 = c.createOscillator();
  const gain1 = c.createGain();
  const filt1 = c.createBiquadFilter();

  osc1.type = 'triangle';
  osc1.frequency.setValueAtTime(311, t);        // Eb4
  osc1.frequency.exponentialRampToValueAtTime(233, t + 0.18); // Bb3

  filt1.type = 'lowpass';
  filt1.frequency.setValueAtTime(800, t);
  filt1.frequency.exponentialRampToValueAtTime(300, t + 0.3);
  filt1.Q.value = 2;

  gain1.gain.setValueAtTime(0, t);
  gain1.gain.linearRampToValueAtTime(sfxVolume * 0.5, t + 0.006);
  gain1.gain.setValueAtTime(sfxVolume * 0.45, t + 0.08);
  gain1.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
  gain1.gain.linearRampToValueAtTime(0, t + 0.37);

  osc1.connect(filt1);
  filt1.connect(gain1);
  gain1.connect(c.destination);
  if (!isMobileWebView()) gain1.connect(getReverb());
  osc1.start(t);
  osc1.stop(t + 0.39);

  // Layer 2: low weight. 80Hz does not exist on a phone speaker; 165Hz
  // is the lowest note the hardware can actually say.
  playTone({
    freq: 165, type: 'sine', startTime: t,
    attack: 0.006, sustain: 0, release: 0.22,
    volume: 0.26, reverb: false,
  });
}

// ── TIMER TICK ─────────────────────────────────────────
// Clean, tension-building click — gets more intense each tick
// Varies pitch based on urgency
export function sfxTick(urgency: number = 0) {
  if (sfxMuted) return;
  const c = getCtx();
  const t = c.currentTime;

  // Base frequency rises with urgency (0-5 scale)
  const baseFreq = 1200 + urgency * 150;

  // Primary click: sharp transient
  const osc = c.createOscillator();
  const gain = c.createGain();
  const filter = c.createBiquadFilter();

  osc.type = 'sine';
  osc.frequency.value = baseFreq;

  filter.type = 'highpass';
  filter.frequency.value = 800;

  const vol = sfxVolume * (0.15 + urgency * 0.05);
  gain.gain.setValueAtTime(vol, t);
  gain.gain.exponentialRampToValueAtTime(0.001, t + 0.035);

  osc.connect(filter);
  filter.connect(gain);
  gain.connect(c.destination);
  osc.start(t);
  osc.stop(t + 0.04);

  // Secondary: short noise transient for "click" texture
  playNoise({ startTime: t, duration: 0.015, volume: 0.03 + urgency * 0.01, filterFreq: 6000, filterType: 'highpass' });
}

// ── STREAK ACHIEVED ────────────────────────────────────
// Exciting ascending celebration with sparkle cascade
// Pentatonic run → bright peak with shimmer tail
export function sfxStreak() {
  if (sfxMuted) return;
  const c = getCtx();
  const t = c.currentTime;

  // Pentatonic celebration: C5 → D5 → E5 → G5 → C6
  const notes = [523, 587, 659, 784, 1047];
  const spacing = 0.055;

  notes.forEach((freq, i) => {
    const noteTime = t + i * spacing;
    const isLast = i === notes.length - 1;

    // Primary tone
    playTone({
      freq, type: 'sine', startTime: noteTime,
      attack: 0.003, sustain: isLast ? 0.12 : 0.03, release: isLast ? 0.5 : 0.08,
      volume: 0.3 + i * 0.05,
      pan: -0.3 + i * 0.15, // Sweep left to right
    });

    // Octave shimmer on each note
    playTone({
      freq: freq * 2, type: 'sine', startTime: noteTime,
      attack: 0.003, sustain: 0.02, release: isLast ? 0.4 : 0.06,
      volume: 0.08 + (isLast ? 0.05 : 0),
      detune: isLast ? 5 : 0,
    });
  });

  // Final sparkle burst at peak
  const peakTime = t + notes.length * spacing;
  playNoise({ startTime: peakTime - 0.02, duration: 0.15, volume: 0.07, filterFreq: 10000, filterType: 'highpass' });

  // Sub-bass impact on the final note for weight
  playTone({
    freq: 130, type: 'sine', startTime: peakTime - 0.02,
    attack: 0.005, sustain: 0.05, release: 0.15,
    volume: 0.15, reverb: false,
  });
}

// ── TIME'S UP ──────────────────────────────────────────
// Dramatic descending warning — two-note "dun-dun"
// Deep, cinematic, slightly ominous
export function sfxTimeUp() {
  if (sfxMuted) return;
  const c = getCtx();
  const t = c.currentTime;

  // Note 1: D4 with grit
  playTone({
    freq: 293, type: 'sawtooth', startTime: t,
    attack: 0.01, sustain: 0.12, release: 0.15,
    volume: 0.2,
    filter: { type: 'lowpass', freq: 600, Q: 3 },
  });
  playTone({
    freq: 293, type: 'triangle', startTime: t,
    attack: 0.01, sustain: 0.12, release: 0.15,
    volume: 0.2,
  });

  // Note 2: Bb3 (tritone tension)
  playTone({
    freq: 233, type: 'sawtooth', startTime: t + 0.2,
    attack: 0.01, sustain: 0.15, release: 0.25,
    volume: 0.25,
    filter: { type: 'lowpass', freq: 500, Q: 3 },
  });
  playTone({
    freq: 233, type: 'triangle', startTime: t + 0.2,
    attack: 0.01, sustain: 0.15, release: 0.25,
    volume: 0.2,
  });

  // Sub-bass impact
  playTone({
    freq: 58, type: 'sine', startTime: t,
    attack: 0.01, sustain: 0.1, release: 0.3,
    volume: 0.25, reverb: false,
  });

  // Noise sweep for drama
  playNoise({ startTime: t, duration: 0.3, volume: 0.05, filterFreq: 400, filterType: 'lowpass' });
}

// ── GAME START ─────────────────────────────────────────
// Energetic "power up" sweep — rising whoosh → bright peak
export function sfxGameStart() {
  if (sfxMuted) return;
  const c = getCtx();
  const t = c.currentTime;

  // Rising sweep
  const osc = c.createOscillator();
  const gain = c.createGain();
  const filter = c.createBiquadFilter();

  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(200, t);
  osc.frequency.exponentialRampToValueAtTime(800, t + 0.25);
  osc.frequency.exponentialRampToValueAtTime(1200, t + 0.3);

  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(400, t);
  filter.frequency.exponentialRampToValueAtTime(3000, t + 0.3);
  filter.Q.value = 4;

  gain.gain.setValueAtTime(0, t);
  gain.gain.linearRampToValueAtTime(0.002, t + 0.01);
  gain.gain.exponentialRampToValueAtTime(sfxVolume * 0.3, t + 0.2);
  gain.gain.exponentialRampToValueAtTime(0.001, t + 0.45);
  gain.gain.linearRampToValueAtTime(0, t + 0.47);

  osc.connect(filter);
  filter.connect(gain);
  gain.connect(c.destination);
  if (!isMobileWebView()) gain.connect(getReverb());
  osc.start(t);
  osc.stop(t + 0.49);

  // Bright peak chime
  playTone({ freq: 1047, type: 'sine', startTime: t + 0.28, attack: 0.005, sustain: 0.05, release: 0.3, volume: 0.25 });
  playTone({ freq: 1568, type: 'sine', startTime: t + 0.28, attack: 0.005, sustain: 0.03, release: 0.2, volume: 0.1 });

  // Noise whoosh
  playNoise({ startTime: t, duration: 0.3, volume: 0.08, filterFreq: 2000, filterType: 'bandpass' });
}

// ── BUTTON TAP ─────────────────────────────────────────
// Subtle, satisfying micro-click — gentler on mobile
export function sfxTap() {
  if (sfxMuted) return;
  const c = getCtx();
  const t = c.currentTime;

  // Softer, lower-pitched tap for mobile
  const freq = isMobileWebView() ? 1200 : 1800;
  const vol = isMobileWebView() ? 0.06 : 0.1;

  playTone({
    freq, type: 'sine', startTime: t,
    attack: 0.001, sustain: 0.008, release: 0.025,
    volume: vol, reverb: false,
  });
  if (!isMobileWebView()) {
    playNoise({ startTime: t, duration: 0.01, volume: 0.02, filterFreq: 5000, filterType: 'highpass' });
  }
}

// ── COUNTDOWN BEEP ─────────────────────────────────────
// Clean beep for 3-2-1 countdown (higher pitch = closer to start)
export function sfxCountdown(step: number) {
  if (sfxMuted) return;
  const freqs = [440, 554, 659, 880]; // A4, C#5, E5, A5
  const freq = freqs[Math.min(step, freqs.length - 1)];
  const c = getCtx();
  const t = c.currentTime;

  playTone({ freq, type: 'sine', startTime: t, attack: 0.003, sustain: 0.08, release: 0.15, volume: 0.3 });
  playTone({ freq: freq * 2, type: 'sine', startTime: t, attack: 0.003, sustain: 0.04, release: 0.1, volume: 0.06 });
}

// ── Haptic Feedback (Capacitor native) ────────────────
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';

export function haptic(type: 'light' | 'medium' | 'error') {
  try {
    switch (type) {
      case 'light': Haptics.impact({ style: ImpactStyle.Light }); break;
      case 'medium': Haptics.impact({ style: ImpactStyle.Medium }); break;
      case 'error': Haptics.notification({ type: NotificationType.Error }); break;
    }
  } catch { /* non-native fallback — silent */ }
}
